import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  GitBranch, ArrowRight, CheckCircle, XCircle, HelpCircle,
  Send, Loader2, ShieldCheck, AlertTriangle, Zap, Ship
} from 'lucide-react';

const CONFIDENCE_STYLE = (c) => {
  if (c >= 0.95) return 'border-green-300 text-green-700 bg-green-50';
  if (c >= 0.80) return 'border-blue-300 text-blue-700 bg-blue-50';
  if (c >= 0.60) return 'border-amber-300 text-amber-700 bg-amber-50';
  return 'border-red-300 text-red-700 bg-red-50';
};

const TIER_STYLE = {
  trade_id_match: { label: 'Trade ID Match', icon: ShieldCheck, color: 'text-green-600' },
  ai_fallback: { label: 'AI Match', icon: Zap, color: 'text-blue-600' },
  manual_override: { label: 'Manual Override', icon: GitBranch, color: 'text-amber-600' },
};

const SIGNAL_LABELS = {
  vessel_identity: { label: 'Vessel', weight: 0.25 },
  date_overlap: { label: 'Date', weight: 0.15 },
  port_matching: { label: 'Port', weight: 0.10 },
  counterparty_match: { label: 'Counterparty', weight: 0.05 },
  cargo_quantity: { label: 'Quantity', weight: 0.05 },
  charge_type_consistency: { label: 'Charge Type', weight: 0.05 },
  trade_id_exact: { label: 'Trade ID', weight: 1.0 },
};

export default function CostAttributionPage() {
  const [tab, setTab] = useState('all');
  const [attributions, setAttributions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [challengeQ, setChallengeQ] = useState('');
  const [challengeAnswer, setChallengeAnswer] = useState(null);
  const [challengeLoading, setChallengeLoading] = useState(false);

  useEffect(() => {
    loadAttributions(tab);
  }, [tab]);

  const loadAttributions = async (status) => {
    setLoading(true);
    try {
      const params = status === 'all' ? {} : { status };
      const { data } = await api.get('/matching/attributions', { params });
      setAttributions(data);
      if (data.length > 0) setSelected(data[0]);
      else setSelected(null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleConfirm = async (id) => {
    try {
      await api.post(`/matching/attributions/${id}/confirm`);
      await loadAttributions(tab);
    } catch (e) { console.error(e); }
  };

  const handleChallenge = async () => {
    if (!selected || !challengeQ.trim()) return;
    setChallengeLoading(true);
    try {
      const { data } = await api.post(`/matching/attributions/${selected.id}/challenge`, { question: challengeQ });
      setChallengeAnswer(data);
      setChallengeQ('');
    } catch (e) { console.error(e); }
    finally { setChallengeLoading(false); }
  };

  const signals = selected?.match_signals || {};
  const tier = TIER_STYLE[selected?.match_tier] || TIER_STYLE.ai_fallback;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="cost-attribution-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Cost Attribution</h1>
        <p className="text-sm text-slate-500 mt-1">Review AI-proposed cost-to-movement matches</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm h-9 bg-slate-100 p-0.5">
          <TabsTrigger value="proposed" className="rounded-sm text-xs" data-testid="tab-proposed">Pending Review</TabsTrigger>
          <TabsTrigger value="confirmed" className="rounded-sm text-xs" data-testid="tab-confirmed">Confirmed</TabsTrigger>
          <TabsTrigger value="all" className="rounded-sm text-xs" data-testid="tab-all-attr">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="grid grid-cols-12 gap-6"><Skeleton className="col-span-4 h-96" /><Skeleton className="col-span-8 h-96" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Attribution List */}
          <div className="lg:col-span-4 space-y-2 max-h-[calc(100vh-14rem)] overflow-y-auto">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 px-1">
              Matches ({attributions.length})
            </p>
            {attributions.length === 0 ? (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardContent className="py-12 text-center">
                  <GitBranch className="h-8 w-8 mx-auto text-slate-200" />
                  <p className="mt-2 text-sm text-slate-400">No attributions found</p>
                </CardContent>
              </Card>
            ) : (
              attributions.map(a => {
                const aTier = TIER_STYLE[a.match_tier] || TIER_STYLE.ai_fallback;
                const TierIcon = aTier.icon;
                return (
                  <Card
                    key={a.id}
                    onClick={() => { setSelected(a); setChallengeAnswer(null); }}
                    data-testid={`attr-card-${a.invoice_number}`}
                    className={`cursor-pointer transition-all rounded-sm shadow-none border ${
                      selected?.id === a.id ? 'border-[#0A2463] bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <CardContent className="p-3 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-bold text-slate-900">{a.invoice_number}</span>
                        <Badge variant="outline" className={`rounded-sm text-[9px] ${CONFIDENCE_STYLE(a.match_confidence || 0)}`}>
                          {((a.match_confidence || 0) * 100).toFixed(0)}%
                        </Badge>
                      </div>
                      <p className="text-[11px] text-slate-500">{a.vendor_name}</p>
                      <div className="flex items-center gap-2">
                        <TierIcon className={`h-3 w-3 ${aTier.color}`} />
                        <span className="text-[10px] text-slate-400">{aTier.label}</span>
                        <ArrowRight className="h-3 w-3 text-slate-200" />
                        <Ship className="h-3 w-3 text-slate-400" />
                        <span className="text-[10px] text-slate-600 truncate">{a.vessel_name}</span>
                      </div>
                      <Badge variant="outline" className={`rounded-sm text-[9px] ${
                        a.status === 'confirmed' ? 'border-green-300 text-green-700' :
                        a.status === 'proposed' ? 'border-amber-300 text-amber-700' :
                        'border-slate-300 text-slate-600'
                      }`}>{a.status}</Badge>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* Right: Detail Panel */}
          <div className="lg:col-span-8 space-y-4">
            {!selected ? (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardContent className="py-24 text-center">
                  <GitBranch className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
                  <p className="mt-4 text-sm text-slate-400">Select an attribution to view match details</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Match Overview */}
                <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <tier.icon className={`h-5 w-5 ${tier.color}`} />
                          <span className="text-sm font-bold text-slate-900">{tier.label}</span>
                          <Badge variant="outline" className={`rounded-sm text-[10px] ${CONFIDENCE_STYLE(selected.match_confidence || 0)}`}>
                            {((selected.match_confidence || 0) * 100).toFixed(0)}% confidence
                          </Badge>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
                          <div><span className="text-slate-400">Invoice: </span><span className="font-mono font-bold">{selected.invoice_number}</span></div>
                          <div><span className="text-slate-400">Vessel: </span><span className="font-mono font-bold">{selected.vessel_name}</span></div>
                          <div><span className="text-slate-400">Vendor: </span><span>{selected.vendor_name}</span></div>
                          <div><span className="text-slate-400">Route: </span><span>{selected.port_load} → {selected.port_discharge}</span></div>
                          <div><span className="text-slate-400">Amount: </span><span className="font-mono font-bold">{selected.currency} {parseFloat(selected.attributed_amount || 0).toLocaleString()}</span></div>
                          <div><span className="text-slate-400">Trade ID: </span><span className="font-mono">{selected.trade_id || '—'}</span></div>
                        </div>
                      </div>
                      {selected.status === 'proposed' && (
                        <Button
                          onClick={() => handleConfirm(selected.id)}
                          data-testid="confirm-match-button"
                          className="rounded-sm bg-[#16A34A] hover:bg-[#16A34A]/90 text-white"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />Confirm Match
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Signal Breakdown */}
                <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="signal-breakdown">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-800">Match Signal Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Object.entries(signals).map(([key, value]) => {
                      const info = SIGNAL_LABELS[key] || { label: key.replace(/_/g, ' '), weight: 0.05 };
                      const pct = Math.round(value * 100);
                      return (
                        <div key={key} className="space-y-1" data-testid={`signal-${key}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-slate-700">{info.label}</span>
                              <span className="text-[10px] text-slate-400">(weight: {info.weight})</span>
                            </div>
                            <span className={`text-xs font-mono font-bold ${pct >= 80 ? 'text-green-700' : pct >= 50 ? 'text-amber-700' : 'text-red-700'}`}>
                              {pct}%
                            </span>
                          </div>
                          <Progress
                            value={pct}
                            className="h-1.5 rounded-sm"
                          />
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Match Reasoning */}
                <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-800">Match Reasoning</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-slate-600 leading-relaxed">{selected.matching_reasoning || 'No reasoning available.'}</p>
                  </CardContent>
                </Card>

                {/* Challenge AI */}
                <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="challenge-ai-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <HelpCircle className="h-4 w-4 text-slate-400" />Challenge AI
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        value={challengeQ}
                        onChange={(e) => setChallengeQ(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleChallenge()}
                        placeholder="e.g. Why wasn't this matched to a different movement?"
                        className="rounded-sm flex-1 text-xs"
                        data-testid="challenge-input"
                      />
                      <Button
                        onClick={handleChallenge}
                        disabled={challengeLoading}
                        data-testid="challenge-submit"
                        className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white"
                      >
                        {challengeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                    {challengeAnswer && (
                      <div className="p-4 border border-slate-200 rounded-sm bg-slate-50" data-testid="challenge-answer">
                        <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{challengeAnswer.explanation}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
